import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from '../../Services/firebase-config';
import { doc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';

const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    const number = parseFloat(value);
    if (isNaN(number)) return value.toString();
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const SummaryPage = () => {
    const [user, setUser] = useState(null);
    const [kibaloName, setKibaloName] = useState('');
    const [costData, setCostData] = useState({
        'Establishment': 0,
        'Equipment': 0,
        'Maintenance': 0,
        'Interest': 0,
        'Variable': 0
    });
    const [totalCost, setTotalCost] = useState(0);
    const [annualYield, setAnnualYield] = useState(0);
    const [grossRevenue, setGrossRevenue] = useState([]);
    const [housingCosts, setHousingCosts] = useState([]);
    const [equipmentCosts, setEquipmentCosts] = useState([]);
    const [otherStartupCosts, setOtherStartupCosts] = useState([]);
    const [yieldPerAcre, setYieldPerAcre] = useState('');
    const [establishmentCosts, setEstablishmentCosts] = useState([]);
    const [depreciationCosts, setDepreciationCosts] = useState([]);
    const [otherFixedCosts, setOtherFixedCosts] = useState([]);
    const [interestCosts, setInterestCosts] = useState([]);
    const [variableCosts, setVariableCosts] = useState([]);

    const navigate = useNavigate();
    const { kibaloId } = useParams();
    const cleanKibaloId = kibaloId ? kibaloId.split('/')[0] : kibaloId;

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            if (authUser) setUser(authUser);
            else navigate('/login');
        });
        return () => unsubscribe();
    }, [navigate]);

    const fetchData = useCallback(async () => {
        if (!user || !cleanKibaloId) return;

        try {
            const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
            const kibaloSnap = await getDoc(kibaloRef);

            if (kibaloSnap.exists()) {
                const data = kibaloSnap.data();
                setKibaloName(data.name || data.enterpriseName);

                // Calculate costs, using 'amount' for Equipment as requested
                const calculatedCosts = {
                    'Establishment': data.establishmentTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
                    'Equipment': data.equipmentCosts?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0, // Changed here
                    'Maintenance': data.maintenanceTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
                    'Interest': data.interestTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
                    'Variable': data.variableCosts?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0
                };

                setCostData(calculatedCosts);
                setTotalCost(Object.values(calculatedCosts).reduce((sum, value) => sum + value, 0));

                if (data.yieldPerAcre) setYieldPerAcre(data.yieldPerAcre);
                if (data.unitCostPerAcre) setAnnualYield(data.unitCostPerAcre);
                if (data.grossRevenue) setGrossRevenue(data.grossRevenue);
                if (data.housingCosts) setHousingCosts(data.housingCosts);
                if (data.equipmentCosts) setEquipmentCosts(data.equipmentCosts);
                if (data.otherStartupCosts) setOtherStartupCosts(data.otherStartupCosts);
                if (data.establishmentTable) setEstablishmentCosts(data.establishmentTable);
                if (data.depreciationTable) setDepreciationCosts(data.depreciationTable);
                if (data.maintenanceTable) setOtherFixedCosts(data.maintenanceTable);
                if (data.interestTable) setInterestCosts(data.interestTable);
                if (data.variableCosts) setVariableCosts(data.variableCosts);
            }
        } catch (error) {
            console.error("Error fetching data for SummaryPage:", error);
        }
    }, [user, cleanKibaloId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const totalInputCost =
        (establishmentCosts.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)) +
        (depreciationCosts.reduce((sum, i) => sum + (parseFloat(i.depreciationAmount) || 0), 0)) +
        (otherFixedCosts.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)) +
        (interestCosts.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)) +
        (variableCosts.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0));

     /* =======================
       ✅ ADDED FUNCTION ONLY
       ======================= */
    const handleDownloadExcel = () => {
        const workbook = XLSX.utils.book_new();

        const addSheet = (name, data, isDepreciation = false) => {
            if (!data || data.length === 0) return;

            const sheetData = data.map(item => ({
                "Cost Center": item.costCenter,
                "Unit": item.unit,
                "Quantity": item.quantity,
                "Unit Cost": item.unitCost,
                "Amount": isDepreciation
                    ? (parseFloat(item.quantity || 0) * parseFloat(item.unitCost || 0))
                    : item.amount
            }));
            const worksheet = XLSX.utils.json_to_sheet(sheetData);
            XLSX.utils.book_append_sheet(workbook, worksheet, name);
        };

        addSheet("Establishment Costs", establishmentCosts);
        addSheet("Depreciation Costs", depreciationCosts, true);
        addSheet("Other Fixed Costs", otherFixedCosts);
        addSheet("Interest Costs", interestCosts);
        addSheet("Variable Costs", variableCosts);

        const summarySheet = XLSX.utils.json_to_sheet([
            { Category: "Total Input Cost", Amount: totalInputCost }
        ]);

        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

        XLSX.writeFile(workbook, `${kibaloName || 'Enterprise'}_Summary.xlsx`);
    };
    /* ======================= */

    

    const renderCostTable = (title, costsArray) => (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h2 className="font-bold text-lg mb-2">{title}</h2>
            <table className="w-full border-collapse border border-gray-300 mb-2">
                <thead>
                    <tr className="bg-gray-200">
                        <th className="border px-3 py-2">Cost Center</th>
                        <th className="border px-3 py-2">Unit</th>
                        <th className="border px-3 py-2">Quantity</th>
                        <th className="border px-3 py-2">Unit Cost</th>
                        <th className="border px-3 py-2">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {costsArray.length > 0 ? costsArray.map((item, idx) => (
                        <tr key={idx}>
                            <td className="border px-3 py-2">{item.costCenter}</td>
                            <td className="border px-3 py-2">{item.unit}</td>
                            <td className="border px-3 py-2">{item.quantity}</td>
                            <td className="border px-3 py-2">{formatNumberWithCommas(item.unitCost)}</td>
                            <td className="border px-3 py-2">{formatNumberWithCommas(item.amount)}</td>
                        </tr>
                    )) : (
                        <tr>
                            <td className="border px-3 py-2 text-center" colSpan={5}>No costs added yet</td>
                        </tr>
                    )}
                    <tr className="bg-gray-200 font-bold">
                        <td className="border px-3 py-2">Total</td>
                        <td className="border px-3 py-2" colSpan={3}></td>
                        <td className="border px-3 py-2">
                            {formatNumberWithCommas(costsArray.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0))}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

    const renderDepreciationTable = (title, costsArray) => (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h2 className="font-bold text-lg mb-2">{title}</h2>
            <table className="w-full border-collapse border border-gray-300 mb-2">
                <thead>
                    <tr className="bg-gray-200">
                        <th className="border px-3 py-2">Cost Center</th>
                        <th className="border px-3 py-2">Unit</th>
                        <th className="border px-3 py-2">Quantity</th>
                        <th className="border px-3 py-2">Unit Cost</th>
                        <th className="border px-3 py-2">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {costsArray.length > 0 ? costsArray.map((item, idx) => (
                        <tr key={idx}>
                            <td className="border px-3 py-2">{item.costCenter}</td>
                            <td className="border px-3 py-2">{item.unit}</td>
                            <td className="border px-3 py-2">{item.quantity}</td>
                            <td className="border px-3 py-2">{formatNumberWithCommas(item.unitCost)}</td>
                            <td className="border px-3 py-2">{formatNumberWithCommas(parseFloat(item.quantity || '0') * parseFloat(item.unitCost || '0'))}</td>
                        </tr>
                    )) : (
                        <tr>
                            <td className="border px-3 py-2 text-center" colSpan={5}>No costs added yet</td>
                        </tr>
                    )}
                    <tr className="bg-gray-200 font-bold">
                        <td className="border px-3 py-2">Total</td>
                        <td className="border px-3 py-2" colSpan={3}></td>
                        <td className="border px-3 py-2">
                            {formatNumberWithCommas(costsArray.reduce((sum, i) => sum + (parseFloat(i.depreciationAmount) || 0), 0))}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

    const renderOtherTables = (title, costsArray) => (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h2 className="font-bold text-lg mb-2">{title}</h2>
            {renderCostTable(null, costsArray)}
        </div>
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-800 uppercase mb-6">
                    Summary for {kibaloName} ENTERPRISE
                </h1>

                {/* Detailed Cost Tables */}
                {renderCostTable('ESTABLISHMENT COSTS', establishmentCosts)}
                {renderDepreciationTable('DEPRECIATION COSTS', depreciationCosts)}
                {renderOtherTables('OTHER ANNUAL FIXED COSTS', otherFixedCosts)}
                {renderOtherTables('INTEREST COSTS', interestCosts)}
                {renderOtherTables('VARIABLE COSTS', variableCosts)}


                {/* Total Input Cost */}
                <div className="mt-8 p-4 bg-indigo-50 rounded-lg shadow-inner border border-indigo-200">
                    <h2 className="font-bold text-lg mb-2 text-indigo-800">Total Input Cost</h2>
                    <p className="text-xl font-bold text-indigo-600">{formatNumberWithCommas(totalInputCost)}</p>
                </div>

                <div className="mt-6 flex justify-between">
                    <button
                        onClick={() => navigate(`/variable-costing/${cleanKibaloId}`)}
                        className="px-4 py-2 rounded-md shadow text-sm font-medium text-white bg-gray-500 hover:bg-gray-600"
                    >
                        Back to Variable Costs
                    </button>
                    <button
                        onClick={handleDownloadExcel}
                        className="px-4 py-2 rounded-md shadow text-sm font-medium text-white bg-teal-600 hover:bg-teal-700"
                    >
                        Download as Excel
                    </button>
                    <button
                        onClick={() => navigate(`/cost-breakdown/${cleanKibaloId}`)}
                        className="px-4 py-2 rounded-md shadow text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                    >
                        Go to Gross Profit Page
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SummaryPage;